import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, ApiFail } from "../api/client";
import type { AuthResponse } from "../api/types";
import { useSession } from "../session";
import { AuthShell } from "../components/AuthShell";
import { Input } from "@grackle/ui";
import { FormNotice, LoadingButton } from "../components/form";
import { OAuthButtons } from "../components/OAuthButtons";
import { ssoStartUrl } from "../lib/sso";

// §2.10 validation rules
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const PASSWORD_MIN = 10;
const PASSWORD_MAX = 128;

export default function SignUp() {
  const navigate = useNavigate();
  const { setUser } = useSession();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [nameError, setNameError] = useState<string | undefined>();
  const [emailError, setEmailError] = useState<string | undefined>();
  const [passwordError, setPasswordError] = useState<string | undefined>();
  const [formError, setFormError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);
  // F13c-1 / SD42: signup for an address at a verified, SSO-enforced domain returns 403
  // sso_required + org_slug; the account is created by JIT on the SSO callback instead.
  const [ssoOrgSlug, setSsoOrgSlug] = useState<string | null>(null);

  function validate(): boolean {
    let ok = true;

    const trimmedName = name.trim();
    if (trimmedName.length < 1 || trimmedName.length > 200) {
      setNameError("Enter your name");
      ok = false;
    } else {
      setNameError(undefined);
    }

    if (!EMAIL_RE.test(email.trim())) {
      setEmailError("Enter a valid email address");
      ok = false;
    } else {
      setEmailError(undefined);
    }

    if (password.length < PASSWORD_MIN || password.length > PASSWORD_MAX) {
      setPasswordError(`At least ${PASSWORD_MIN} characters`);
      ok = false;
    } else {
      setPasswordError(undefined);
    }

    return ok;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(undefined);
    setSsoOrgSlug(null);
    if (!validate()) return;

    setSubmitting(true);
    try {
      const res = await api<AuthResponse>("POST", "/auth/signup", {
        email: email.trim().toLowerCase(),
        password,
        name: name.trim(),
      });
      setUser(res.user);
      navigate("/verify", { replace: true });
    } catch (err) {
      if (err instanceof ApiFail) {
        if (err.code === "conflict") {
          setEmailError("An account with this email already exists");
        } else if (err.code === "validation") {
          setFormError(err.message);
        } else if (err.code === "rate_limited") {
          setFormError("Too many attempts. Wait a moment and try again.");
        } else if (err.code === "sso_required") {
          const slug = err.data?.org_slug;
          if (typeof slug === "string" && slug) {
            setSsoOrgSlug(slug);
          } else {
            setFormError("Your organization requires single sign-on for this email address.");
          }
        } else {
          setFormError(err.message);
        }
      } else {
        setFormError("Couldn't reach Grackle. Check your connection and try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell
      title="Create your account"
      subtitle="Record your screen and share a link before you finish talking about it."
      footer={
        <>
          By creating an account you agree to the terms and the privacy notice. Already have
          one? <Link to="/signin">Sign in</Link>.
        </>
      }
    >
      {ssoOrgSlug ? (
        <div className="auth-form">
          <FormNotice>
            Your organization requires single sign-on for this email address. Continue with SSO to
            create your account.
          </FormNotice>
          <LoadingButton block onClick={() => { window.location.href = ssoStartUrl(ssoOrgSlug); }}>
            Continue with SSO
          </LoadingButton>
          <LoadingButton type="button" variant="ghost" block onClick={() => setSsoOrgSlug(null)}>
            Use a different email
          </LoadingButton>
        </div>
      ) : (
        <>
          <form className="auth-form" onSubmit={onSubmit} noValidate>
            <Input
              label="Name"
              type="text"
              autoComplete="name"
              placeholder="Wesley Hurd"
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={nameError}
            />
            <Input
              label="Work email"
              type="email"
              autoComplete="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={emailError}
            />
            <Input
              label="Password"
              type="password"
              autoComplete="new-password"
              placeholder="••••••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              help={`At least ${PASSWORD_MIN} characters`}
              error={passwordError}
            />
            {formError ? <FormNotice tone="error">{formError}</FormNotice> : null}
            <LoadingButton type="submit" block loading={submitting}>
              {submitting ? "Creating account…" : "Create account"}
            </LoadingButton>
          </form>
          <OAuthButtons />
        </>
      )}
    </AuthShell>
  );
}
